import { AbortError } from '@app/errors/AbortError'
import { AppError } from '@app/errors/AppError'

import { sleep } from '@app/utils/sleep'

import { type AuthSessionBroadcast, type AuthSessionManagerInterface, type AuthSessionSnapshot } from '@app/packages/api/AuthSessionManager'
import { ApiError, RequestError } from '@app/packages/api/errors'

/**
 * Refresh once the token enters the last 20% of its lifetime. Expressed as a
 * fraction so the refresh lead scales with the real `access_expires_in` the
 * backend issues instead of a fixed duration that would misbehave (refresh on
 * every request) the moment the token becomes shorter-lived than the lead.
 */
export const SESSION_SKEW_FRACTION = 0.2

export type LegacyExchangeParams = {
  access_token: string
  client_id: string
  client_type: 'web' | 'mobile' | 'ios' | 'android'
  device_id: string
}

export type AuthSessionManagerInit = {
  refreshUrl: string
  exchangeUrl?: string
  loginUrl?: string
  fetch: typeof fetch
  initialSnapshot: AuthSessionSnapshot | null
  onAuthLost: () => void | Promise<void>
  onSessionRefreshed?: (snapshot: AuthSessionSnapshot) => void | Promise<void>
  broadcast?: AuthSessionBroadcast | null
  getExtraHeaders?: () => Record<string, string> | Promise<Record<string, string>>
  getLegacyExchangeParams?: () => LegacyExchangeParams | null | Promise<LegacyExchangeParams | null>
  /**
   * Reports whether refresh material exists for the current session. When it
   * returns `false` and no legacy exchange material is available:
   * - with no cached token, `ensureAccessToken()` resolves to `null` (anonymous);
   * - with a cached (now stale) token, the session is unrecoverable — auth loss
   *   fires immediately and the pending request is aborted rather than sent
   *   under a different (anonymous) identity.
   * Default behavior (when omitted) is to always attempt the refresh.
   */
  hasRefreshSession?: () => boolean | Promise<boolean>
  /**
   * A token from somewhere that is not this manager, asked for when there is no refresh material
   * of our own. In the app's webview the refresh token lives in the app; a stale access token
   * there is not a lost session, it is one this page cannot renew by itself.
   *
   * - a snapshot — adopt it and carry on;
   * - `null` — the source says nobody is signed in. An answer, not a failure: the session is
   *   given up as it would be for a refresh that came back empty;
   * - a throw — the source could not answer (no bridge, a timeout, an old app). The current
   *   state is held: the request fails, and nothing is torn down on the strength of not knowing.
   */
  requestExternalToken?: () => Promise<AuthSessionSnapshot | null>
  /**
   * Refresh when less than this fraction of the token's lifetime remains,
   * scaling the lead with the actual expiry. Takes precedence over `skewMs`
   * whenever a token lifetime is known (see {@link SESSION_SKEW_FRACTION}).
   */
  skewFraction?: number
  skewMs?: number
  maxRefreshAttempts?: number
  /** Backoff between throttled retries, used only when the 429 carries no usable `Retry-After`. */
  getRetryDelayMs?: (attempt: number) => number
  /** Fail a 429 outright rather than honouring a `Retry-After` longer than this. */
  maxRetryAfterMs?: number
  now?: () => number
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
}

const DEFAULT_SKEW_MS = 30_000
/** A throttle window is outwaited or it isn't — one deferred retry, never a burst. */
const DEFAULT_MAX_ATTEMPTS = 2
const DEFAULT_RETRY_DELAY = (attempt: number) => [5_000, 10_000][attempt] ?? 10_000
const DEFAULT_MAX_RETRY_AFTER_MS = 10_000

export class AuthSessionManager implements AuthSessionManagerInterface, Disposable {
  private readonly init: AuthSessionManagerInit
  private accessToken: string | null = null
  private expiresAt: number | null = null
  private tokenLifetimeMs: number | null = null
  private inflight: Promise<string | null> | null = null
  private broadcastSub: Disposable | null = null
  private triedExchange = false
  private sessionLost = false

  constructor(init: AuthSessionManagerInit) {
    this.init = init
    if (init.initialSnapshot) {
      this.accessToken = init.initialSnapshot.access_token
      this.expiresAt = init.initialSnapshot.expires_at
      // No `access_expires_in` for a seeded snapshot — approximate the lifetime
      // by the time left until expiry (≈ full lifetime for a freshly issued one).
      this.tokenLifetimeMs = init.initialSnapshot.expires_at - (init.now ?? Date.now)()
    }
    if (init.broadcast) {
      this.broadcastSub = init.broadcast.addListener(snapshot => {
        if (snapshot === null) {
          // Peer tab signalled auth loss — clear in-memory state. Do not
          // invoke onAuthLost here: the originating tab is responsible for
          // broadcasting the explicit logout / reload signal.
          this.accessToken = null
          this.expiresAt = null
          return
        }
        if (this.expiresAt !== null && snapshot.expires_at <= this.expiresAt) return
        this.accessToken = snapshot.access_token
        this.expiresAt = snapshot.expires_at
        this.tokenLifetimeMs = snapshot.expires_at - (this.init.now ?? Date.now)()
        this.sessionLost = false
        void this.init.onSessionRefreshed?.(snapshot)
      })
    }
  }

  [Symbol.dispose](): void {
    this.broadcastSub?.[Symbol.dispose]()
    this.broadcastSub = null
  }

  async ensureAccessToken(options?: { signal?: AbortSignal }): Promise<string | null> {
    const now = (this.init.now ?? Date.now)()
    if (this.accessToken !== null && this.expiresAt !== null && now < this.expiresAt - this.currentSkewMs()) {
      return this.accessToken
    }
    try {
      return await this.refresh(options)
    } catch (error) {
      if (error instanceof ApiError && error.handled) return null
      throw error
    }
  }

  peekAccessToken(): string | null {
    return this.accessToken
  }

  invalidateAccessToken(): void {
    this.accessToken = null
    this.expiresAt = null
  }

  async forceRefresh(options?: { signal?: AbortSignal }): Promise<string> {
    const token = await this.refresh(options)
    if (token === null) {
      throw new ApiError('AuthSessionManager.forceRefresh: no refresh material available').withStatus(0)
    }
    return token
  }

  /** Unlike refresh/exchange, a 4xx is thrown to the caller, not treated as auth loss. */
  async login(attributes: Record<string, unknown>, options?: { signal?: AbortSignal }): Promise<string> {
    if (!this.init.loginUrl) throw new AppError('AuthSessionManager.login: loginUrl not configured')
    return this.callTokenEndpoint('login', this.init.loginUrl, JSON.stringify({ data: { type: 'sessions', attributes } }), {
      signal: options?.signal,
      treatClientErrorAsAuthLost: false,
    })
  }

  /** Seed a freshly obtained snapshot (e.g. right after a login exchange) without a round-trip. */
  adoptSnapshot(snapshot: AuthSessionSnapshot): void {
    this.accessToken = snapshot.access_token
    this.expiresAt = snapshot.expires_at
    this.tokenLifetimeMs = snapshot.expires_at - (this.init.now ?? Date.now)()
    this.sessionLost = false
    void this.init.onSessionRefreshed?.(snapshot)
    this.init.broadcast?.send(snapshot)
  }

  /**
   * How long to hold before retrying a throttled token request: the server's
   * `Retry-After` when it sends a usable one, else our own backoff. Null when the
   * server asks for longer than we are willing to stall — a window we can't outwait
   * is better failed immediately than retried into.
   */
  private throttleWaitMs(resp: Response, attempt: number): number | null {
    const advertised = parseRetryAfter(resp.headers.get('retry-after'), (this.init.now ?? Date.now)())
    if (advertised === null) return (this.init.getRetryDelayMs ?? DEFAULT_RETRY_DELAY)(attempt)
    return advertised > (this.init.maxRetryAfterMs ?? DEFAULT_MAX_RETRY_AFTER_MS) ? null : advertised
  }

  /** Refresh lead: a fraction of the token's real lifetime when known, else the fixed `skewMs`/default. */
  private currentSkewMs(): number {
    if (this.init.skewFraction !== undefined && this.tokenLifetimeMs !== null) {
      return this.tokenLifetimeMs * this.init.skewFraction
    }
    return this.init.skewMs ?? DEFAULT_SKEW_MS
  }

  private refresh(options?: { signal?: AbortSignal }): Promise<string | null> {
    if (this.inflight) return this.inflight
    this.inflight = this.doRefresh(options).finally(() => {
      this.inflight = null
    })
    return this.inflight
  }

  private async doRefresh(options?: { signal?: AbortSignal }): Promise<string | null> {
    // The owner may keep claiming the old session until its reload lands (the client leaves
    // redux intact for that), and asking again would only repeat the refusal and the teardown.
    if (this.sessionLost) throw new ApiError('Auth session is already lost').withStatus(401).withHandled(true)
    const exchange = await this.consumeExchangeParams()
    if (exchange) {
      return this.callTokenEndpoint('exchange', exchange.url, JSON.stringify({ data: { type: 'sessions', attributes: exchange.params } }), options)
    }
    if ((await this.init.hasRefreshSession?.()) === false) {
      if (this.accessToken === null) return null

      // Somebody else may hold refresh material even though we do not — in the app's webview,
      // the app does. Ask before concluding the session is gone.
      if (this.init.requestExternalToken) {
        const external = await this.init.requestExternalToken()
        if (external) {
          this.adoptSnapshot(external)
          await this.init.onSessionRefreshed?.(external)
          return external.access_token
        }
        // A `null` is the source saying nobody is signed in, so it falls through to the same
        // teardown a rejected refresh gets. A throw does not: it propagates, the request fails,
        // and the session is left alone because we do not know that it ended.
      }

      // A stale token with no refresh material is the same terminal state as a
      // rejected refresh: log out immediately and abort the pending request.
      await this.loseSession()
      throw new AbortError('Access token is stale and no refresh session is available')
    }
    return this.callTokenEndpoint('refresh', this.init.refreshUrl, JSON.stringify({ data: { type: 'sessions', attributes: {} } }), options)
  }

  private async consumeExchangeParams(): Promise<{ url: string; params: LegacyExchangeParams } | null> {
    if (this.triedExchange) return null
    if (!this.init.exchangeUrl || !this.init.getLegacyExchangeParams) return null
    const params = await this.init.getLegacyExchangeParams()
    if (!params) return null
    this.triedExchange = true
    return { url: this.init.exchangeUrl, params }
  }

  private async callTokenEndpoint(
    label: 'refresh' | 'exchange' | 'login',
    url: string,
    body: string,
    options?: { signal?: AbortSignal; treatClientErrorAsAuthLost?: boolean }
  ): Promise<string> {
    const treatClientErrorAsAuthLost = options?.treatClientErrorAsAuthLost ?? true
    const maxAttempts = this.init.maxRefreshAttempts ?? DEFAULT_MAX_ATTEMPTS
    const doSleep = this.init.sleep ?? sleep

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let resp: Response
      try {
        resp = await this.init.fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            ...((await this.init.getExtraHeaders?.()) ?? {}),
            'content-type': 'application/vnd.api+json',
            'x-requested-with': 'XMLHttpRequest',
          },
          body,
          signal: options?.signal,
        })
      } catch (e) {
        throw new RequestError(e instanceof Error ? e.message : String(e), { cause: e })
      }

      // Retrying inside the throttle window only re-arms it — the limiter counts the
      // requests it rejects too. Hold for the whole window before trying again.
      if (resp.status === 429) {
        const wait = this.throttleWaitMs(resp, attempt)
        if (wait === null || attempt + 1 >= maxAttempts) {
          throw new ApiError(`Auth ${label} failed: 429`).withStatus(429)
        }
        await doSleep(wait, options?.signal)
        continue
      }

      // Any other client error means the session can't be refreshed/exchanged —
      // a 401 (invalid refresh token) or a 400 (no refresh token, e.g. a session
      // entered via a URL JWT with no kidsout_rt). Treat it as lost rather than
      // retrying the same doomed request on every subsequent call.
      if (resp.status >= 400 && resp.status < 500) {
        if (!treatClientErrorAsAuthLost) {
          throw new ApiError(`Auth ${label} failed: ${resp.status}`).withStatus(resp.status)
        }
        await this.loseSession()
        // Auth loss is already handled here (logout + reload); flag the error so
        // reporters don't surface this expected terminal state to Sentry.
        const error = new ApiError(`Auth ${label} failed: ${resp.status}`).withStatus(resp.status)
        error.handled = true
        throw error
      }

      if (!resp.ok) {
        throw new ApiError(`Auth ${label} failed: ${resp.status}`).withStatus(resp.status)
      }

      const json = await resp.json()
      const access_token = json?.data?.attributes?.access_token
      const access_expires_in = json?.data?.attributes?.access_expires_in
      const user_id = json?.data?.relationships?.subject?.data?.id
      // A 2xx with a malformed body is an upstream contract violation, not a
      // client-meaningful HTTP status — throw an AppError (generic server error)
      // rather than echoing the success status onto the error page. Distinct
      // message per field so Sentry groups them and pinpoints what was missing.
      if (typeof access_token !== 'string') throw new AppError(`Auth ${label} response missing access_token`)
      if (typeof access_expires_in !== 'number') throw new AppError(`Auth ${label} response missing access_expires_in`)
      if (typeof user_id !== 'string') throw new AppError(`Auth ${label} response missing subject id`)

      const now = (this.init.now ?? Date.now)()
      const expires_at = now + access_expires_in * 1000
      this.accessToken = access_token
      this.expiresAt = expires_at
      this.tokenLifetimeMs = access_expires_in * 1000
      this.sessionLost = false

      const snapshot: AuthSessionSnapshot = { access_token, expires_at, user_id }
      await this.init.onSessionRefreshed?.(snapshot)
      this.init.broadcast?.send(snapshot)

      return access_token
    }

    throw new ApiError(`Auth ${label}: max attempts exceeded`).withStatus(429)
  }

  private async loseSession(): Promise<void> {
    this.sessionLost = true
    this.accessToken = null
    this.expiresAt = null
    this.init.broadcast?.send(null)
    await this.init.onAuthLost()
  }
}

/** `Retry-After` is either delta-seconds or an HTTP-date (RFC 9110 §10.2.3). */
function parseRetryAfter(header: string | null, now: number): number | null {
  const raw = header?.trim()
  if (!raw) return null
  const seconds = Number(raw)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  const date = Date.parse(raw)
  if (Number.isNaN(date)) return null
  return Math.max(0, date - now)
}
